import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Send, Play, Square, FileText, Users, MapPin, Clock, CheckCircle, XCircle, Download, Eye, Mail, Pencil, RotateCcw, Ban } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status labels
const MEETING_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800', icon: FileText },
  CONVOCATA: { label: 'Convocata', color: 'bg-blue-100 text-blue-800', icon: Send },
  IN_CORSO: { label: 'In Corso', color: 'bg-green-100 text-green-800', icon: Play },
  CONCLUSA: { label: 'Conclusa', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
  ANNULLATA: { label: 'Annullata', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const MEETING_TYPES = {
  ORDINARIA: 'Ordinaria',
  STRAORDINARIA: 'Straordinaria',
  URGENTE: 'Urgente'
};

export const Meetings = () => {
  const { token, selectedCondominium } = useAuth();
  const navigate = useNavigate();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  
  // State
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingDetail, setMeetingDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isNewMeetingOpen, setIsNewMeetingOpen] = useState(false);
  // assemblea in modifica (null = se ne sta creando una nuova)
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isMinutesOpen, setIsMinutesOpen] = useState(false);
  
  // Form state
  const [meetingForm, setMeetingForm] = useState({
    meeting_type: 'ORDINARIA',
    title: '',
    date_time_first: '',
    date_time_second: '',
    location: '',
    notes: '',
    odg_items: [{ number: 1, title: '', description: '', requires_vote: false }]
  });
  
  // Minutes form - secondo normativa condominiale
  const [minutesForm, setMinutesForm] = useState({
    // Dati iniziali dell'assemblea
    convocation_type: 'PRIMA', // PRIMA, SECONDA
    president_name: '',
    secretary_name: '',
    
    // Testo apertura con formula standard
    opening_text: '',
    
    // Verifica presenze e deleghe
    attendees_verified: false,
    digital_minutes_accepted: false, // "I condomini accettano la redazione del presente verbale in formato digitale"
    
    // Delibere
    resolutions: [],
    
    // Chiusura
    closing_text: '',
    next_meeting_date: ''
  });

  // Fetch functions
  const fetchMeetings = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setMeetings(await res.json());
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
    setLoading(false);
  }, [condoId, token]);

  const fetchMeetingDetail = async (meetingId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${meetingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMeetingDetail(data);
        setSelectedMeeting(data.meeting);
        
        // Initialize minutes form from existing minutes
        if (data.minutes) {
          setMinutesForm({
            // Nuovi campi per conformità normativa
            convocation_type: data.minutes.convocation_type || 'PRIMA',
            president_name: data.minutes.president_name || '',
            secretary_name: data.minutes.secretary_name || '',
            attendees_verified: data.minutes.attendees_verified || false,
            digital_minutes_accepted: data.minutes.digital_minutes_accepted || false,
            // Campi esistenti
            opening_text: data.minutes.opening_text || '',
            resolutions: data.minutes.resolutions || [],
            closing_text: data.minutes.closing_text || '',
            next_meeting_date: data.minutes.next_meeting_date || ''
          });
        } else {
          // Initialize resolutions from ODG
          const resolutions = data.meeting.odg_items?.map(item => ({
            odg_number: item.number,
            title: item.title,
            description: '',
            votes_favor: 0,
            votes_against: 0,
            votes_abstain: 0,
            millesimi_favor: 0,
            millesimi_against: 0,
            millesimi_abstain: 0,
            passed: false
          })) || [];
          setMinutesForm({
            convocation_type: 'PRIMA',
            president_name: '',
            secretary_name: '',
            attendees_verified: false,
            digital_minutes_accepted: false,
            opening_text: '',
            resolutions,
            closing_text: '',
            next_meeting_date: ''
          });
        }
        
        setIsDetailOpen(true);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Create or update meeting (stesso modulo: in modifica si fa la PUT)
  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    const modifica = !!editingMeetingId;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings${modifica ? `/${editingMeetingId}` : ''}`, {
        method: modifica ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(meetingForm)
      });
      if (res.ok) {
        toast.success(modifica ? 'Assemblea aggiornata' : 'Assemblea creata');
        setIsNewMeetingOpen(false);
        setEditingMeetingId(null);
        resetMeetingForm();
        fetchMeetings();
      } else {
        const error = await res.json();
        toast.error(error.detail || (modifica ? 'Errore modifica' : 'Errore creazione'));
      }
    } catch (error) {
      toast.error(modifica ? 'Errore modifica' : 'Errore creazione');
    }
  };

  // Apre il modulo con i dati dell'assemblea da modificare (bozza o convocata)
  const openEditMeeting = (meeting) => {
    setMeetingForm({
      meeting_type: meeting.meeting_type || 'ORDINARIA',
      title: meeting.title || '',
      date_time_first: (meeting.date_time_first || '').slice(0, 16),
      date_time_second: (meeting.date_time_second || '').slice(0, 16),
      location: meeting.location || '',
      notes: meeting.notes || '',
      odg_items: (meeting.odg_items && meeting.odg_items.length)
        ? meeting.odg_items.map((o, i) => ({ number: o.number || i + 1, title: o.title || '', description: o.description || '', requires_vote: !!o.requires_vote }))
        : [{ number: 1, title: '', description: '', requires_vote: false }]
    });
    setEditingMeetingId(meeting.id);
    setIsNewMeetingOpen(true);
  };

  // Busta: prepara (o ritrova) la bozza di convocazione nel Centro Comunicazioni, con il PDF
  // aggiornato alle date attuali dell'assemblea, e apre subito «Verifica e invia»
  const handleInviaLettere = async (meetingId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${meetingId}/convocation-draft`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.communication_id) {
        toast.success(data.created ? 'Bozza di convocazione preparata con il PDF aggiornato' : 'Riapro la bozza di convocazione esistente');
        navigate(`/communications-center/${condoId}?apri=${data.communication_id}`);
      } else {
        toast.error(data.detail || 'Non riesco a preparare la convocazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  // Revoca la convocazione: torna in bozza, via presenze e PDF generato dall'app
  const handleRevokeConvocation = async (meetingId) => {
    if (!window.confirm('Revocare la convocazione? L\'assemblea torna in bozza: potrai modificarla e riconvocarla. Le lettere già spedite restano nello storico.')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${meetingId}/revoke`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Convocazione revocata: assemblea in bozza');
        fetchMeetings();
        setIsDetailOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella revoca');
      }
    } catch (error) {
      toast.error('Errore nella revoca');
    }
  };

  // Annulla l'assemblea (resta in elenco come annullata)
  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Annullare questa assemblea? Resta in elenco come annullata e non si può più tenere.')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${meetingId}/cancel`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Assemblea annullata');
        fetchMeetings();
        setIsDetailOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nell\'annullamento');
      }
    } catch (error) {
      toast.error('Errore nell\'annullamento');
    }
  };

  // Send convocation
  const handleSendConvocation = async (meetingId) => {
    if (!window.confirm('Inviare la convocazione? Verranno create le schede presenza.')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${meetingId}/send-convocation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        fetchMeetings();
        if (meetingDetail?.meeting?.id === meetingId) {
          fetchMeetingDetail(meetingId);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore invio');
      }
    } catch (error) {
      toast.error('Errore invio');
    }
  };

  // Start meeting
  const handleStartMeeting = async (meetingId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${meetingId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Assemblea iniziata');
        fetchMeetings();
        fetchMeetingDetail(meetingId);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // End meeting
  const handleEndMeeting = async (meetingId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${meetingId}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Assemblea conclusa');
        fetchMeetings();
        fetchMeetingDetail(meetingId);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Update attendance
  const handleUpdateAttendance = async (attendanceId, present, attendeeName) => {
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/meetings/${selectedMeeting.id}/attendance/${attendanceId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ present, attendee_name: attendeeName })
        }
      );
      if (res.ok) {
        fetchMeetingDetail(selectedMeeting.id);
      }
    } catch (error) {
      toast.error('Errore aggiornamento');
    }
  };

  // Save minutes
  const handleSaveMinutes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${selectedMeeting.id}/minutes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(minutesForm)
      });
      if (res.ok) {
        toast.success('Verbale salvato');
        setIsMinutesOpen(false);
        fetchMeetingDetail(selectedMeeting.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore salvataggio');
    }
  };

  // Generate minutes PDF
  const handleGenerateMinutesPDF = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/meetings/${selectedMeeting.id}/generate-minutes-pdf`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore generazione PDF');
      }
    } catch (error) {
      toast.error('Errore generazione PDF');
    }
  };

  // Delete meeting
  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Eliminare definitivamente questa assemblea? Vengono tolti anche le presenze e il PDF di convocazione generato dall\'app.')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/meetings/${meetingId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Assemblea eliminata');
        fetchMeetings();
        setIsDetailOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore eliminazione');
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Form helpers
  const resetMeetingForm = () => {
    setMeetingForm({
      meeting_type: 'ORDINARIA',
      title: '',
      date_time_first: '',
      date_time_second: '',
      location: '',
      notes: '',
      odg_items: [{ number: 1, title: '', description: '', requires_vote: false }]
    });
  };

  const addOdgItem = () => {
    setMeetingForm({
      ...meetingForm,
      odg_items: [
        ...meetingForm.odg_items,
        { number: meetingForm.odg_items.length + 1, title: '', description: '', requires_vote: false }
      ]
    });
  };

  const removeOdgItem = (index) => {
    const newItems = meetingForm.odg_items.filter((_, i) => i !== index).map((item, i) => ({
      ...item,
      number: i + 1
    }));
    setMeetingForm({ ...meetingForm, odg_items: newItems });
  };

  const updateOdgItem = (index, field, value) => {
    const newItems = [...meetingForm.odg_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setMeetingForm({ ...meetingForm, odg_items: newItems });
  };

  // Format helpers
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const canEdit = userRole === 'ADMIN';

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Assemblee" subtitle="Convocazioni e verbali" />
      {canEdit && (
        <div className="flex flex-wrap gap-2 justify-end">
          <Dialog open={isNewMeetingOpen} onOpenChange={(aperto) => { setIsNewMeetingOpen(aperto); if (!aperto) { setEditingMeetingId(null); resetMeetingForm(); } }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Convoca Assemblea
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingMeetingId ? 'Modifica Assemblea' : 'Nuova Assemblea'}</DialogTitle>
                <DialogDescription>{editingMeetingId ? 'Titolo, date, luogo, ordine del giorno e note: le modifiche valgono anche se l\'assemblea è già convocata' : 'Crea una nuova assemblea condominiale'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateMeeting} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo *</Label>
                    <Select
                      value={meetingForm.meeting_type}
                      onValueChange={(v) => setMeetingForm({ ...meetingForm, meeting_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(MEETING_TYPES).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Titolo *</Label>
                    <Input
                      value={meetingForm.title}
                      onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                      placeholder="Es: Assemblea Ordinaria 2025"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prima Convocazione *</Label>
                    <Input
                      type="datetime-local"
                      value={meetingForm.date_time_first}
                      onChange={(e) => setMeetingForm({ ...meetingForm, date_time_first: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Seconda Convocazione</Label>
                    <Input
                      type="datetime-local"
                      value={meetingForm.date_time_second}
                      onChange={(e) => setMeetingForm({ ...meetingForm, date_time_second: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Luogo *</Label>
                  <Input
                    value={meetingForm.location}
                    onChange={(e) => setMeetingForm({ ...meetingForm, location: e.target.value })}
                    placeholder="Es: Sala condominiale, Via Roma 1"
                    required
                  />
                </div>

                {/* ODG Items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Ordine del Giorno</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addOdgItem}>
                      <Plus className="h-4 w-4 mr-1" /> Aggiungi punto
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {meetingForm.odg_items.map((item, index) => (
                      <Card key={index} className="p-3">
                        <div className="flex gap-3">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-medium">
                            {item.number}
                          </div>
                          <div className="flex-1 space-y-2">
                            <Input
                              value={item.title}
                              onChange={(e) => updateOdgItem(index, 'title', e.target.value)}
                              placeholder="Titolo punto ODG"
                            />
                            <Input
                              value={item.description}
                              onChange={(e) => updateOdgItem(index, 'description', e.target.value)}
                              placeholder="Descrizione (opzionale)"
                            />
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={item.requires_vote}
                                onCheckedChange={(v) => updateOdgItem(index, 'requires_vote', v)}
                              />
                              <span className="text-sm">Richiede votazione</span>
                            </div>
                          </div>
                          {meetingForm.odg_items.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeOdgItem(index)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Note</Label>
                  <Textarea
                    value={meetingForm.notes}
                    onChange={(e) => setMeetingForm({ ...meetingForm, notes: e.target.value })}
                    placeholder="Note aggiuntive..."
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsNewMeetingOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" data-testid="assemblea-salva">{editingMeetingId ? 'Salva modifiche' : 'Crea Assemblea'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        )}

      {/* Meetings List */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Assemblee</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
          ) : meetings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna assemblea programmata</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Titolo</TableHead>
                  <TableHead>Data/Ora</TableHead>
                  <TableHead>Luogo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {meetings.map((meeting) => {
                  const statusInfo = MEETING_STATUS[meeting.status] || MEETING_STATUS.DRAFT;
                  const StatusIcon = statusInfo.icon;
                  return (
                    <TableRow key={meeting.id} className="cursor-pointer hover:bg-slate-50">
                      <TableCell>
                        <Badge variant="outline">{MEETING_TYPES[meeting.meeting_type]}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{meeting.title}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          {formatDateTime(meeting.date_time_first)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {meeting.location}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => fetchMeetingDetail(meeting.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canEdit && ['DRAFT', 'CONVOCATA'].includes(meeting.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleInviaLettere(meeting.id)}
                              title="Prepara la convocazione (PDF con le date attuali) e apri «Verifica e invia»: PEC / email / stampa / bacheca"
                              data-testid={`invia-lettere-${meeting.id}`}
                            >
                              <Mail className="h-4 w-4 text-green-700" />
                            </Button>
                          )}
                          {canEdit && ['DRAFT', 'CONVOCATA'].includes(meeting.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditMeeting(meeting)}
                              title="Modifica (titolo, date, luogo, ordine del giorno)"
                              data-testid={`modifica-assemblea-${meeting.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canEdit && meeting.status === 'DRAFT' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSendConvocation(meeting.id)}
                              title="Segna come convocata (genera il PDF e le presenze)"
                            >
                              <Send className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          {canEdit && meeting.status === 'CONVOCATA' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStartMeeting(meeting.id)}
                                title="Inizia assemblea"
                              >
                                <Play className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRevokeConvocation(meeting.id)}
                                title="Revoca la convocazione (torna in bozza)"
                                data-testid={`revoca-assemblea-${meeting.id}`}
                              >
                                <RotateCcw className="h-4 w-4 text-amber-600" />
                              </Button>
                            </>
                          )}
                          {canEdit && ['DRAFT', 'CONVOCATA'].includes(meeting.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleCancelMeeting(meeting.id)}
                              title="Annulla l'assemblea (resta in elenco come annullata)"
                              data-testid={`annulla-assemblea-${meeting.id}`}
                            >
                              <Ban className="h-4 w-4 text-stone-500" />
                            </Button>
                          )}
                          {canEdit && ['DRAFT', 'CONVOCATA', 'ANNULLATA'].includes(meeting.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteMeeting(meeting.id)}
                              title="Elimina definitivamente"
                              data-testid={`elimina-assemblea-${meeting.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                          {canEdit && meeting.status === 'IN_CORSO' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEndMeeting(meeting.id)}
                              title="Termina assemblea"
                            >
                              <Square className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Meeting Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedMeeting?.title}</DialogTitle>
            <DialogDescription>
              {formatDateTime(selectedMeeting?.date_time_first)} • {selectedMeeting?.location}
            </DialogDescription>
          </DialogHeader>
          
          {meetingDetail && (
            <Tabs defaultValue="info" className="space-y-4">
              <TabsList>
                <TabsTrigger value="info">Informazioni</TabsTrigger>
                <TabsTrigger value="attendance">Presenze ({meetingDetail.quorum_info?.total_present || 0}/{meetingDetail.quorum_info?.total_invited || 0})</TabsTrigger>
                <TabsTrigger value="minutes">Verbale</TabsTrigger>
              </TabsList>

              {/* Info Tab */}
              <TabsContent value="info" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">Tipo</p>
                    <p className="font-medium">{MEETING_TYPES[selectedMeeting.meeting_type]}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">Stato</p>
                    <Badge className={MEETING_STATUS[selectedMeeting.status]?.color}>
                      {MEETING_STATUS[selectedMeeting.status]?.label}
                    </Badge>
                  </div>
                </div>
                
                {/* Quorum Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Quorum</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold">{meetingDetail.quorum_info.total_present}</p>
                        <p className="text-xs text-muted-foreground">Presenti</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{meetingDetail.quorum_info.total_absent}</p>
                        <p className="text-xs text-muted-foreground">Assenti</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{meetingDetail.quorum_info.millesimi_percentage}%</p>
                        <p className="text-xs text-muted-foreground">Millesimi</p>
                      </div>
                      <div>
                        <Badge className={meetingDetail.quorum_info.has_quorum ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {meetingDetail.quorum_info.has_quorum ? 'Quorum Raggiunto' : 'Quorum Non Raggiunto'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* ODG */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Ordine del Giorno</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedMeeting.odg_items?.map((item, i) => (
                        <div key={i} className="flex gap-3 p-2 bg-slate-50 rounded">
                          <span className="font-medium">{item.number}.</span>
                          <div>
                            <p>{item.title}</p>
                            {item.description && <p className="text-sm text-muted-foreground">{item.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Attendance Tab */}
              <TabsContent value="attendance" className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unità</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Millesimi</TableHead>
                      <TableHead>Presente</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {meetingDetail.attendance?.map((att) => (
                      <TableRow key={att.id}>
                        <TableCell className="font-medium">{att.unit_number}</TableCell>
                        <TableCell>
                          {canEdit && selectedMeeting.status !== 'CONCLUSA' ? (
                            <Input
                              value={att.attendee_name}
                              onChange={(e) => handleUpdateAttendance(att.id, att.present, e.target.value)}
                              className="h-8 w-40"
                              placeholder="Nome partecipante"
                            />
                          ) : (
                            att.attendee_name || '-'
                          )}
                        </TableCell>
                        <TableCell>{att.millesimi.toFixed(2)}</TableCell>
                        <TableCell>
                          {canEdit && selectedMeeting.status !== 'CONCLUSA' ? (
                            <Checkbox
                              checked={att.present}
                              onCheckedChange={(v) => handleUpdateAttendance(att.id, v, att.attendee_name)}
                            />
                          ) : (
                            att.present ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-600" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>

              {/* Minutes Tab */}
              <TabsContent value="minutes" className="space-y-4">
                {canEdit && (
                  <div className="flex gap-2">
                    <Button onClick={() => setIsMinutesOpen(true)}>
                      <FileText className="h-4 w-4 mr-2" />
                      Modifica Verbale
                    </Button>
                    {meetingDetail.minutes && (
                      <Button variant="outline" onClick={handleGenerateMinutesPDF}>
                        <Download className="h-4 w-4 mr-2" />
                        Genera PDF
                      </Button>
                    )}
                  </div>
                )}
                
                {meetingDetail.minutes ? (
                  <div className="space-y-4">
                    {meetingDetail.minutes.opening_text && (
                      <p className="text-sm">{meetingDetail.minutes.opening_text}</p>
                    )}
                    
                    {meetingDetail.minutes.resolutions?.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Delibere</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {meetingDetail.minutes.resolutions.map((res, i) => (
                            <div key={i} className="p-3 bg-slate-50 rounded-lg">
                              <p className="font-medium">Punto {res.odg_number}: {res.title}</p>
                              <p className="text-sm text-muted-foreground">
                                Favorevoli: {res.votes_favor} • Contrari: {res.votes_against} • Astenuti: {res.votes_abstain}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Millesimi — favorevoli: {(res.millesimi_favor || 0).toFixed(2)} •
                                contrari: {(res.millesimi_against || 0).toFixed(2)} •
                                astenuti: {(res.millesimi_abstain || 0).toFixed(2)}
                              </p>
                              <Badge className={res.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {res.passed ? 'Approvata' : 'Respinta'}
                              </Badge>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                    
                    {meetingDetail.minutes.closing_text && (
                      <p className="text-sm">{meetingDetail.minutes.closing_text}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Verbale non ancora redatto</p>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Minutes Edit Dialog - Secondo Normativa Condominiale */}
      <Dialog open={isMinutesOpen} onOpenChange={setIsMinutesOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Redazione Verbale di Assemblea</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Compila il verbale secondo la normativa condominiale vigente
            </p>
          </DialogHeader>
          <div className="space-y-6">
            {/* Sezione 1: Dati Assemblea */}
            <Card className="p-4 bg-slate-50">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Dati dell'Assemblea
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Convocazione</Label>
                  <Select
                    value={minutesForm.convocation_type}
                    onValueChange={(v) => setMinutesForm({ ...minutesForm, convocation_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo convocazione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PRIMA">Prima Convocazione</SelectItem>
                      <SelectItem value="SECONDA">Seconda Convocazione</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data e Ora Apertura</Label>
                  <Input 
                    type="datetime-local" 
                    value={selectedMeeting?.date_time_first || ''} 
                    disabled 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Presidente dell'Assemblea *</Label>
                  <Input
                    value={minutesForm.president_name}
                    onChange={(e) => setMinutesForm({ ...minutesForm, president_name: e.target.value })}
                    placeholder="Nome e cognome del presidente"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Segretario Verbalizzante *</Label>
                  <Input
                    value={minutesForm.secretary_name}
                    onChange={(e) => setMinutesForm({ ...minutesForm, secretary_name: e.target.value })}
                    placeholder="Nome e cognome del segretario"
                  />
                </div>
              </div>
            </Card>

            {/* Sezione 2: Verifica Presenze */}
            <Card className="p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Verifica Presenze e Quorum
              </h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="attendees_verified"
                    checked={minutesForm.attendees_verified}
                    onCheckedChange={(c) => setMinutesForm({ ...minutesForm, attendees_verified: c })}
                  />
                  <label htmlFor="attendees_verified" className="text-sm">
                    Verificata la regolarità delle presenze e delle deleghe
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="digital_minutes"
                    checked={minutesForm.digital_minutes_accepted}
                    onCheckedChange={(c) => setMinutesForm({ ...minutesForm, digital_minutes_accepted: c })}
                  />
                  <label htmlFor="digital_minutes" className="text-sm">
                    I condomini accettano la redazione del presente verbale in formato digitale e la sottoscrizione per via telematica
                  </label>
                </div>
              </div>
            </Card>
            
            {/* Sezione 3: Testo Apertura */}
            <div className="space-y-2">
              <Label>Testo di Apertura</Label>
              <Textarea
                value={minutesForm.opening_text}
                onChange={(e) => setMinutesForm({ ...minutesForm, opening_text: e.target.value })}
                placeholder={`In data ${selectedMeeting?.date_time_first?.split('T')[0] || '...'}, alle ore ${selectedMeeting?.date_time_first?.split('T')[1]?.substring(0,5) || '...'}, presso ${selectedMeeting?.location || '...'}, si è riunita l'assemblea dei condomini del ${selectedCondominium?.condominium?.name || 'Condominio'} in ${minutesForm.convocation_type === 'PRIMA' ? 'prima' : 'seconda'} convocazione, regolarmente convocata.\n\nAssume la presidenza il Sig./Sig.ra ${minutesForm.president_name || '...'} e funge da segretario verbalizzante il Sig./Sig.ra ${minutesForm.secretary_name || '...'}.\n\nVerificate le presenze e le deleghe, si constata che l'assemblea è validamente costituita.`}
                rows={6}
              />
            </div>
            
            {/* Sezione 4: Delibere */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Delibere dell'Assemblea</Label>
              {minutesForm.resolutions.map((res, i) => (
                <Card key={i} className="p-4">
                  <p className="font-medium mb-3">
                    Punto {res.odg_number} O.d.G.: {res.title}
                  </p>
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div>
                      <Label className="text-xs text-green-600">Favorevoli</Label>
                      <Input
                        type="number"
                        value={res.votes_favor}
                        onChange={(e) => {
                          const newRes = [...minutesForm.resolutions];
                          newRes[i].votes_favor = parseInt(e.target.value) || 0;
                          setMinutesForm({ ...minutesForm, resolutions: newRes });
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-red-600">Contrari</Label>
                      <Input
                        type="number"
                        value={res.votes_against}
                        onChange={(e) => {
                          const newRes = [...minutesForm.resolutions];
                          newRes[i].votes_against = parseInt(e.target.value) || 0;
                          setMinutesForm({ ...minutesForm, resolutions: newRes });
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Astenuti</Label>
                      <Input
                        type="number"
                        value={res.votes_abstain}
                        onChange={(e) => {
                          const newRes = [...minutesForm.resolutions];
                          newRes[i].votes_abstain = parseInt(e.target.value) || 0;
                          setMinutesForm({ ...minutesForm, resolutions: newRes });
                        }}
                      />
                    </div>
                  </div>
                  {/* I millesimi sono decimali: e' il quorum che conta davvero,
                      e troncarli a intero falsa la verifica della delibera. */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <Label className="text-xs text-green-600">Millesimi favorevoli</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={res.millesimi_favor ?? 0}
                        onChange={(e) => {
                          const newRes = [...minutesForm.resolutions];
                          newRes[i].millesimi_favor = parseFloat(e.target.value) || 0;
                          setMinutesForm({ ...minutesForm, resolutions: newRes });
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-red-600">Millesimi contrari</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={res.millesimi_against ?? 0}
                        onChange={(e) => {
                          const newRes = [...minutesForm.resolutions];
                          newRes[i].millesimi_against = parseFloat(e.target.value) || 0;
                          setMinutesForm({ ...minutesForm, resolutions: newRes });
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Millesimi astenuti</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={res.millesimi_abstain ?? 0}
                        onChange={(e) => {
                          const newRes = [...minutesForm.resolutions];
                          newRes[i].millesimi_abstain = parseFloat(e.target.value) || 0;
                          setMinutesForm({ ...minutesForm, resolutions: newRes });
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 mb-2">
                    Totale millesimi presenti:{' '}
                    {(
                      (res.millesimi_favor || 0) +
                      (res.millesimi_against || 0) +
                      (res.millesimi_abstain || 0)
                    ).toFixed(2)}
                    /1000
                  </p>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={res.passed}
                      onCheckedChange={(v) => {
                        const newRes = [...minutesForm.resolutions];
                        newRes[i].passed = v;
                        setMinutesForm({ ...minutesForm, resolutions: newRes });
                      }}
                    />
                    <span className="text-sm">Delibera approvata</span>
                  </div>
                </Card>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Testo Chiusura</Label>
              <Textarea
                value={minutesForm.closing_text}
                onChange={(e) => setMinutesForm({ ...minutesForm, closing_text: e.target.value })}
                placeholder="Non essendovi altro da discutere..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Prossima Assemblea</Label>
              <Input
                type="date"
                value={minutesForm.next_meeting_date}
                onChange={(e) => setMinutesForm({ ...minutesForm, next_meeting_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMinutesOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveMinutes}>Salva Verbale</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
